(() => {
  "use strict";

  const GAME_COPY = {
    "catch-the-punt": {
      icon: "👐",
      title: "Catch the Punt",
      description: "Slide the returner under each falling football. Catch as many as possible in 30 seconds.",
      instructions: "Drag left and right, move your mouse, or use the arrow keys."
    },
    "running-back": {
      icon: "🏃",
      title: "Running Back Challenge",
      description: "Juke through defenders and keep gaining yards for 30 seconds.",
      instructions: "Drag left and right or use the arrow keys. Avoid every defender."
    },
    "field-goal": {
      icon: "🥅",
      title: "Field Goal Challenge",
      description: "Swipe upward from the football. Adjust your angle for the wind.",
      instructions: "Swipe upward from the ball. Five kicks make one scored attempt."
    },
    "tackle-game": {
      icon: "🎯",
      title: "Tackle Timing Game",
      description: "Tap when the moving ball carrier reaches the tackle zone.",
      instructions: "Tap the field or press the space bar. Ten attempts make one score."
    }
  };

  const waitForSupabase = async () => {
    for (let i = 0; i < 100; i += 1) {
      if (window.supabase && typeof window.supabase.createClient === "function") {
        return window.supabase;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    throw new Error("Supabase JavaScript could not be loaded.");
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const formatNumber = (value, maximumFractionDigits = 0) =>
    new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(Number(value || 0));

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric"
    }).format(date);
  };

  const safeGameUrl = (value) => {
    const url = String(value || "").trim();
    if (!url) return "";
    if ((url.startsWith("/") && !url.startsWith("//")) || url.startsWith("https://") || url.startsWith("http://")) {
      return url;
    }
    return "";
  };

  const proofMimeType = (file) => {
    if (file?.type) return file.type;
    const extension = String(file?.name || "").split(".").pop().toLowerCase();
    const types = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      webp: "image/webp",
      heic: "image/heic",
      heif: "image/heif",
      mp4: "video/mp4",
      mov: "video/quicktime",
      webm: "video/webm"
    };
    return types[extension] || "";
  };

  class TreasureStateChallenge {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector("[data-tsc-config]").textContent);
      this.edgeUrl =
        this.config.edgeFunctionUrl ||
        `${String(this.config.supabaseUrl || "").replace(/\/$/, "")}/functions/v1/app-api`;

      this.client = null;
      this.session = null;
      this.bootstrapData = null;
      this.meData = null;
      this.triviaData = null;
      this.triviaIndex = 0;
      this.triviaStartedAt = 0;
      this.activeLeaderTab = "individual";
      this.activeChallenge = null;
      this.currentGame = null;
      this.gameSessionId = null;
      this.gameState = null;
      this.gameRaf = 0;
      this.gameRunning = false;
      this.lastFrame = 0;
      this.statusTimer = 0;
      this.passwordRecoveryMode = false;

      this.canvas = root.querySelector("[data-game-canvas]");
      this.ctx = this.canvas?.getContext("2d") || null;
    }

    async init() {
      if (!this.config.supabaseUrl || !this.config.supabaseAnonKey || !this.edgeUrl) {
        this.showStatus(
          "Add the Supabase URL, publishable/anon key, and Edge Function URL in this section’s Shopify settings.",
          "error",
          0
        );
        return;
      }

      const supabaseLibrary = await waitForSupabase();
      this.client = supabaseLibrary.createClient(
        this.config.supabaseUrl,
        this.config.supabaseAnonKey,
        {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true
          }
        }
      );

      this.bindEvents();

      const sessionResult = await this.client.auth.getSession();
      this.session = sessionResult.data.session;
      this.updateAuthUi();

      this.client.auth.onAuthStateChange((event, session) => {
        this.session = session;
        if (event === "PASSWORD_RECOVERY") {
          this.passwordRecoveryMode = true;
          this.openAuth();
          this.showAuthPanel("update-password");
        }
        window.setTimeout(() => this.handleAuthChange(), 0);
      });

      await this.loadBootstrap();
      if (this.session) {
        await this.loadMe();
      } else {
        this.renderProfile();
      }
    }

    bindEvents() {
      this.root.addEventListener("click", (event) => {
        const target = event.target.closest("button, [data-view-target]");
        if (!target) return;

        const view = target.dataset.viewTarget;
        if (view) {
          this.showView(view);
          return;
        }

        if (target.dataset.authViewTarget) {
          this.showAuthPanel(target.dataset.authViewTarget);
          return;
        }

        const action = target.dataset.action;
        if (action === "open-auth") this.openAuth();
        if (action === "close-modal") this.closeAuth();
        if (action === "sign-out") this.signOut();
        if (action === "refresh-leaderboards") this.refreshLeaderboards();
        if (action === "close-game") this.closeGame();
        if (action === "start-game") this.startCurrentGame();
        if (action === "close-challenge") this.closeChallenge();

        if (target.dataset.gameSlug) this.openGame(target.dataset.gameSlug);
        if (target.dataset.challengeId) this.openChallenge(target.dataset.challengeId);
        if (target.dataset.leaderTab) {
          this.activeLeaderTab = target.dataset.leaderTab;
          this.root.querySelectorAll("[data-leader-tab]").forEach((button) => {
            button.classList.toggle("is-active", button === target);
          });
          this.renderLeaderboard();
        }
        if (target.dataset.answerIndex !== undefined) {
          this.submitTriviaAnswer(Number(target.dataset.answerIndex), target);
        }
      });

      this.root.querySelector("[data-auth-login-form]")?.addEventListener("submit", (event) =>
        this.signInWithPassword(event)
      );
      this.root.querySelector("[data-auth-signup-form]")?.addEventListener("submit", (event) =>
        this.signUpWithPassword(event)
      );
      this.root.querySelector("[data-auth-reset-form]")?.addEventListener("submit", (event) =>
        this.sendPasswordReset(event)
      );
      this.root.querySelector("[data-auth-update-password-form]")?.addEventListener("submit", (event) =>
        this.updateRecoveredPassword(event)
      );
      this.root.querySelector("[data-profile-form]")?.addEventListener("submit", (event) =>
        this.saveProfile(event)
      );
      this.root.querySelector("[data-challenge-form]")?.addEventListener("submit", (event) =>
        this.submitChallenge(event)
      );

      if (this.canvas) {
        this.canvas.addEventListener("pointerdown", (event) => this.onGamePointerDown(event));
        this.canvas.addEventListener("pointermove", (event) => this.onGamePointerMove(event));
        this.canvas.addEventListener("pointerup", (event) => this.onGamePointerUp(event));
        this.canvas.addEventListener("pointercancel", (event) => this.onGamePointerUp(event));
      }

      window.addEventListener("keydown", (event) => this.onGameKey(event));
    }

    async handleAuthChange() {
      this.updateAuthUi();
      if (this.session) {
        if (!this.passwordRecoveryMode) this.closeAuth();
        await this.loadMe();
      } else {
        this.meData = null;
        this.renderMySummary();
        this.renderProfile();
      }
    }

    async api(action, options = {}) {
      const method = options.method || "GET";
      const url = new URL(this.edgeUrl);
      const headers = {
        apikey: this.config.supabaseAnonKey
      };

      if (this.session?.access_token) {
        headers.Authorization = `Bearer ${this.session.access_token}`;
      }

      const fetchOptions = { method, headers };

      if (method === "GET") {
        url.searchParams.set("action", action);
        Object.entries(options.query || {}).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            url.searchParams.set(key, String(value));
          }
        });
      } else {
        headers["Content-Type"] = "application/json";
        fetchOptions.body = JSON.stringify({ action, ...(options.body || {}) });
      }

      const response = await fetch(url.toString(), fetchOptions);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(payload.error || "The request could not be completed.");
        error.code = payload.code || "";
        throw error;
      }

      return payload;
    }

    async loadBootstrap() {
      try {
        this.bootstrapData = await this.api("bootstrap");
        this.renderAll();
      } catch (error) {
        this.showStatus(error.message, "error", 0);
      }
    }

    async loadMe() {
      if (!this.session) return;
      try {
        this.meData = await this.api("me");
        this.renderMySummary();
        this.renderProfile();
        if (!this.meData.profile?.school_id) {
          this.showView("profile");
          this.showStatus("Choose your fanbase and display name before earning points.", "error", 7000);
        }
      } catch (error) {
        if (error.code === "AUTH_REQUIRED") {
          await this.client.auth.signOut();
        } else {
          this.showStatus(error.message, "error");
        }
      }
    }

    renderAll() {
      this.renderScoreboard();
      this.renderChallenges();
      this.renderGames();
      this.renderLeaderboard();
      this.renderImpactSummary();
      this.renderProfile();
      this.renderMySummary();
    }

    showView(name) {
      this.root.querySelectorAll("[data-view]").forEach((view) => {
        const active = view.dataset.view === name;
        view.hidden = !active;
        view.classList.toggle("is-active", active);
      });
      this.root.querySelectorAll(".tsc__nav [data-view-target]").forEach((button) => {
        button.classList.toggle("is-active", button.dataset.viewTarget === name);
      });

      if (name === "trivia") this.loadTrivia();
      if (name === "leaderboards") this.refreshLeaderboards(false);
      this.root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    updateAuthUi() {
      const label = this.root.querySelector("[data-auth-label]");
      const signOut = this.root.querySelector('[data-action="sign-out"]');
      if (label) label.textContent = this.session ? "My account" : "Sign in";
      if (signOut) signOut.hidden = !this.session;
    }

    openAuth() {
      if (this.session && !this.passwordRecoveryMode) {
        this.showView("profile");
        return;
      }
      const modal = this.root.querySelector("[data-auth-modal]");
      if (modal) modal.hidden = false;
      if (!this.passwordRecoveryMode) this.showAuthPanel("login");
    }

    closeAuth() {
      const modal = this.root.querySelector("[data-auth-modal]");
      if (modal) modal.hidden = true;
    }

    showAuthPanel(name) {
      this.root.querySelectorAll("[data-auth-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.authPanel !== name;
      });
      this.root.querySelectorAll("[data-auth-view-target]").forEach((button) => {
        button.classList.toggle("is-active", button.dataset.authViewTarget === name);
      });
    }

    async signInWithPassword(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const email = form.elements.email.value.trim();
      const password = form.elements.password.value;
      const button = form.querySelector('button[type="submit"]');

      button.disabled = true;
      try {
        const { error } = await this.client.auth.signInWithPassword({ email, password });
        if (error) throw error;
        this.showStatus("You are signed in.", "success");
      } catch (error) {
        this.showStatus(error.message, "error");
      } finally {
        button.disabled = false;
      }
    }

    async signUpWithPassword(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const button = form.querySelector('button[type="submit"]');
      const email = form.elements.email.value.trim();
      const password = form.elements.password.value;
      const confirmPassword = form.elements.password_confirm.value;
      const profile = {
        display_name: form.elements.display_name.value.trim(),
        school_id: form.elements.school_id.value,
        age_group: form.elements.age_group.value,
        accepted_terms: form.elements.accepted_terms.checked
      };

      if (profile.display_name.length < 2) {
        this.showStatus("Public display name must be at least two characters.", "error");
        return;
      }
      if (password.length < 8) {
        this.showStatus("Password must be at least eight characters.", "error");
        return;
      }
      if (password !== confirmPassword) {
        this.showStatus("The passwords do not match.", "error");
        return;
      }
      if (!profile.school_id) {
        this.showStatus("Choose Montana State or Montana.", "error");
        return;
      }
      if (!profile.accepted_terms) {
        this.showStatus("Accept the competition rules to create an account.", "error");
        return;
      }

      button.disabled = true;
      try {
        const { data, error } = await this.client.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.href.split("#")[0],
            data: {
              display_name: profile.display_name,
              age_group: profile.age_group
            }
          }
        });
        if (error) throw error;

        if (data.session) {
          this.session = data.session;
          await this.api("profile-save", { method: "POST", body: profile });
          await this.loadMe();
          this.closeAuth();
          this.showStatus("Account created. You are signed in and ready to compete.", "success", 7000);
        } else {
          this.showStatus(
            "Account created. Confirm your email, then sign in and finish your profile.",
            "success",
            10000
          );
          this.showAuthPanel("login");
        }
      } catch (error) {
        this.showStatus(error.message, "error");
      } finally {
        button.disabled = false;
      }
    }

    async sendPasswordReset(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const email = form.elements.email.value.trim();
      const button = form.querySelector('button[type="submit"]');
      button.disabled = true;

      try {
        const { error } = await this.client.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.href.split("#")[0]
        });
        if (error) throw error;
        this.showStatus("Password-reset link sent. Check your email.", "success", 8000);
        this.showAuthPanel("login");
      } catch (error) {
        this.showStatus(error.message, "error");
      } finally {
        button.disabled = false;
      }
    }

    async updateRecoveredPassword(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const password = form.elements.password.value;
      const confirmPassword = form.elements.password_confirm.value;
      const button = form.querySelector('button[type="submit"]');

      if (password.length < 8) {
        this.showStatus("Password must be at least eight characters.", "error");
        return;
      }
      if (password !== confirmPassword) {
        this.showStatus("The passwords do not match.", "error");
        return;
      }

      button.disabled = true;
      try {
        const { error } = await this.client.auth.updateUser({ password });
        if (error) throw error;
        this.passwordRecoveryMode = false;
        this.closeAuth();
        this.showStatus("Password updated. You are signed in.", "success", 7000);
      } catch (error) {
        this.showStatus(error.message, "error");
      } finally {
        button.disabled = false;
      }
    }

    async signOut() {
      await this.client.auth.signOut();
      this.showStatus("Signed out.", "success");
    }

    renderScoreboard() {
      const container = this.root.querySelector("[data-school-scoreboard]");
      const rows = this.bootstrapData?.leaderboards?.schools || [];
      const schools = this.bootstrapData?.schools || [];

      if (!container) return;
      if (!rows.length && !schools.length) {
        container.innerHTML = '<div class="tsc__empty">The rivalry scoreboard is waiting for its first points.</div>';
        return;
      }

      const source = rows.length
        ? rows
        : schools.map((school) => ({ ...school, total_points: 0, adjusted_score: 0, active_fans: 0 }));

      container.innerHTML = source
        .map(
          (school) => `
            <article class="tsc__school-row" style="--school-color:${escapeHtml(school.primary_color)}">
              <div>
                <p class="tsc__school-name">${escapeHtml(school.short_name)}</p>
                <div class="tsc__school-meta">
                  <span>${formatNumber(school.active_fans)} active fans</span>
                  <span>${formatNumber(school.adjusted_score, 2)} points per fan</span>
                </div>
              </div>
              <div class="tsc__school-points">
                ${formatNumber(school.total_points)}
                <small>Total points</small>
              </div>
            </article>
          `
        )
        .join("");
    }

    renderMySummary() {
      const points = this.root.querySelector("[data-my-points]");
      const summary = this.root.querySelector("[data-my-summary]");
      if (!points || !summary) return;

      points.textContent = `${formatNumber(this.meData?.total_points || 0)} pts`;

      if (!this.session) {
        summary.innerHTML = '<p>Sign in and select your team to start scoring.</p>';
        return;
      }

      const profile = this.meData?.profile;
      if (!profile?.school_id) {
        summary.innerHTML =
          '<p>Finish your profile to unlock trivia scoring, games, fitness, and community challenges.</p>';
        return;
      }

      const school = this.bootstrapData?.schools?.find((item) => item.id === profile.school_id);
      summary.innerHTML = `
        <p><strong>${escapeHtml(profile.display_name)}</strong> is competing for
          <strong>${escapeHtml(school?.short_name || "your team")}</strong>.
        </p>
        <div class="tsc__meta-row">
          <span class="tsc__meta-chip">${escapeHtml(profile.age_group)}</span>
          <span class="tsc__meta-chip">Daily check-in active</span>
        </div>
      `;
    }

    renderProfile() {
      const form = this.root.querySelector("[data-profile-form]");
      const select = form?.elements.school_id;
      if (!form || !select) return;

      const schoolOptions =
        '<option value="">Choose your team</option>' +
        (this.bootstrapData?.schools || [])
          .map(
            (school) =>
              `<option value="${escapeHtml(school.id)}">${escapeHtml(school.short_name)}</option>`
          )
          .join("");

      const currentValue = this.meData?.profile?.school_id || select.value;
      select.innerHTML = schoolOptions;
      select.value = currentValue || "";

      const signupSelect = this.root.querySelector("[data-signup-school-select]");
      if (signupSelect) {
        const signupValue = signupSelect.value;
        signupSelect.innerHTML = schoolOptions;
        signupSelect.value = signupValue || "";
      }

      const profile = this.meData?.profile;
      form.elements.display_name.value = profile?.display_name || "";
      form.elements.age_group.value = profile?.age_group || "adult";
      form.elements.accepted_terms.checked = Boolean(profile?.accepted_terms);

      const recentContainer = this.root.querySelector("[data-recent-activity]");
      if (!recentContainer) return;

      if (!this.session) {
        recentContainer.innerHTML = "<p>Sign in to see your recent activity.</p>";
        return;
      }

      const recent = this.meData?.recent || [];
      if (!recent.length) {
        recentContainer.innerHTML = '<div class="tsc__empty">Your first points will appear here.</div>';
        return;
      }

      recentContainer.innerHTML = `
        <div class="tsc__activity-list">
          ${recent
            .map(
              (item) => `
                <div class="tsc__activity-item">
                  <div>
                    <strong>${escapeHtml(item.description || item.source_type)}</strong>
                    <span>${formatDate(item.created_at)}</span>
                  </div>
                  <strong>+${formatNumber(item.points)}</strong>
                </div>
              `
            )
            .join("")}
        </div>
      `;
    }

    async saveProfile(event) {
      event.preventDefault();
      if (!this.session) {
        this.openAuth();
        return;
      }

      const form = event.currentTarget;
      const button = form.querySelector('button[type="submit"]');
      button.disabled = true;

      try {
        await this.api("profile-save", {
          method: "POST",
          body: {
            display_name: form.elements.display_name.value,
            school_id: form.elements.school_id.value,
            age_group: form.elements.age_group.value,
            accepted_terms: form.elements.accepted_terms.checked
          }
        });
        await this.loadMe();
        await this.refreshLeaderboards(false);
        this.showStatus("Profile saved. Your team will lock after your first points.", "success");
      } catch (error) {
        this.showStatus(error.message, "error");
      } finally {
        button.disabled = false;
      }
    }

    requireParticipant() {
      if (!this.session) {
        this.openAuth();
        this.showStatus("Sign in before submitting a scored activity.", "error");
        return false;
      }
      if (!this.meData?.profile?.school_id) {
        this.showView("profile");
        this.showStatus("Choose your fanbase before earning points.", "error");
        return false;
      }
      return true;
    }

    async loadTrivia() {
      const container = this.root.querySelector("[data-trivia-container]");
      if (!container) return;

      if (!this.requireParticipant()) {
        container.innerHTML =
          '<div class="tsc__empty">Sign in and complete your profile to play today’s trivia.</div>';
        return;
      }

      container.innerHTML = '<div class="tsc__loading">Loading today’s questions…</div>';

      try {
        this.triviaData = await this.api("trivia");
        this.root.querySelector("[data-trivia-date]").textContent = this.triviaData.date || "Today";
        const nextQuestionIndex = this.triviaData.questions.findIndex(
          (question) => !question.attempt
        );
        this.triviaIndex =
          nextQuestionIndex === -1 ? this.triviaData.questions.length : nextQuestionIndex;
        this.renderTrivia();
      } catch (error) {
        container.innerHTML = `<div class="tsc__empty">${escapeHtml(error.message)}</div>`;
      }
    }

    renderTrivia() {
      const container = this.root.querySelector("[data-trivia-container]");
      const questions = this.triviaData?.questions || [];
      if (!container) return;

      if (!questions.length) {
        container.innerHTML =
          '<div class="tsc__empty">No trivia questions are scheduled for today yet.</div>';
        return;
      }

      const unansweredIndex = questions.findIndex((question) => !question.attempt);
      if (unansweredIndex === -1 || this.triviaIndex >= questions.length) {
        const correct = questions.filter((question) => question.attempt?.correct).length;
        const points = questions.reduce(
          (sum, question) => sum + Number(question.attempt?.points_awarded || 0),
          0
        );
        container.innerHTML = `
          <div class="tsc__empty">
            <h3>Today’s trivia is complete!</h3>
            <p>${correct} of ${questions.length} correct · ${formatNumber(points)} points earned</p>
          </div>
        `;
        return;
      }

      this.triviaIndex = Math.max(this.triviaIndex, unansweredIndex);
      const question = questions[this.triviaIndex];
      if (question.attempt) {
        this.triviaIndex += 1;
        this.renderTrivia();
        return;
      }

      this.triviaStartedAt = performance.now();
      const letters = "ABCDEFGH";
      container.innerHTML = `
        <div class="tsc__question-progress">
          ${questions
            .map(
              (item, index) =>
                `<span class="${item.attempt || index < this.triviaIndex ? "is-complete" : ""}"></span>`
            )
            .join("")}
        </div>
        <div class="tsc__trivia-question">
          <div class="tsc__trivia-meta">
            <span>Question ${this.triviaIndex + 1} of ${questions.length}</span>
            <span>${escapeHtml(question.difficulty)} · ${formatNumber(question.points)} base points</span>
          </div>
          <h3>${escapeHtml(question.prompt)}</h3>
          <div class="tsc__answers">
            ${question.options
              .map(
                (option, index) => `
                  <button type="button" class="tsc__answer" data-answer-index="${index}">
                    <span>${letters[index]}</span>
                    <span>${escapeHtml(option)}</span>
                  </button>
                `
              )
              .join("")}
          </div>
        </div>
      `;
    }

    async submitTriviaAnswer(index, button) {
      if (!this.triviaData || !this.requireParticipant()) return;
      const question = this.triviaData.questions[this.triviaIndex];
      if (!question || question.attempt) return;

      this.root.querySelectorAll("[data-answer-index]").forEach((answer) => {
        answer.disabled = true;
      });

      try {
        const result = await this.api("trivia-submit", {
          method: "POST",
          body: {
            question_id: question.id,
            selected_index: index,
            response_ms: Math.round(performance.now() - this.triviaStartedAt)
          }
        });

        question.attempt = {
          correct: result.is_correct,
          points_awarded: result.points_awarded
        };

        button.classList.add(result.is_correct ? "is-correct" : "is-wrong");
        const correctButton = this.root.querySelector(
          `[data-answer-index="${result.correct_index}"]`
        );
        if (correctButton) correctButton.classList.add("is-correct");

        this.showStatus(
          result.is_correct
            ? `Correct! +${formatNumber(result.points_awarded)} points.`
            : "Not this time. Tomorrow brings another chance.",
          result.is_correct ? "success" : "error",
          2400
        );

        await this.loadMe();
        window.setTimeout(() => {
          this.triviaIndex += 1;
          this.renderTrivia();
        }, 1250);
      } catch (error) {
        this.showStatus(error.message, "error");
        this.root.querySelectorAll("[data-answer-index]").forEach((answer) => {
          answer.disabled = false;
        });
      }
    }

    renderChallenges() {
      const challenges = this.bootstrapData?.challenges || [];
      this.renderChallengeKind(
        "fitness",
        this.root.querySelector("[data-fitness-list]"),
        challenges.filter((challenge) => challenge.kind === "fitness")
      );
      this.renderChallengeKind(
        "impact",
        this.root.querySelector("[data-impact-list]"),
        challenges.filter((challenge) => challenge.kind === "impact")
      );
    }

    renderChallengeKind(kind, container, challenges) {
      if (!container) return;
      if (!challenges.length) {
        container.innerHTML = '<div class="tsc__empty">New challenges are coming soon.</div>';
        return;
      }

      container.innerHTML = challenges
        .map(
          (challenge) => `
            <article class="tsc__card tsc__challenge-card">
              <span class="tsc__card-icon">${kind === "fitness" ? "🏃" : "🤝"}</span>
              <div>
                <p class="tsc__kicker">${kind === "fitness" ? "Fitness" : "For Good"}</p>
                <h2>${escapeHtml(challenge.title)}</h2>
                <p>${escapeHtml(challenge.description)}</p>
              </div>
              <div class="tsc__meta-row">
                <span class="tsc__meta-chip">${formatNumber(challenge.target_value, 1)} ${escapeHtml(challenge.unit)}</span>
                <span class="tsc__meta-chip">${formatNumber(challenge.points)} points</span>
                <span class="tsc__meta-chip">${challenge.verification === "review" ? "Admin reviewed" : "Self verified"}</span>
              </div>
              <button type="button" class="tsc__button" data-challenge-id="${escapeHtml(challenge.id)}">
                Submit completion
              </button>
            </article>
          `
        )
        .join("");
    }

    setChallengeStatus(message = "", type = "") {
      const status = this.root.querySelector("[data-challenge-status]");
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
      status.classList.toggle("is-error", type === "error");
      status.classList.toggle("is-success", type === "success");
    }

    openChallenge(id) {
      if (!this.requireParticipant()) return;
      const challenge = this.bootstrapData?.challenges?.find((item) => item.id === id);
      if (!challenge) return;

      this.activeChallenge = challenge;
      const modal = this.root.querySelector("[data-challenge-modal]");
      const form = this.root.querySelector("[data-challenge-form]");
      const proofInput = form.elements.proof;
      const proofLabel = modal.querySelector("[data-proof-label-text]");
      modal.querySelector("[data-challenge-kind]").textContent =
        challenge.kind === "fitness" ? "Fitness Challenge" : "Community Challenge";
      modal.querySelector("[data-challenge-title]").textContent = challenge.title;
      modal.querySelector("[data-challenge-description]").textContent = challenge.description;
      modal.querySelector("[data-challenge-unit]").textContent = challenge.unit;
      modal.querySelector("[data-verification-note]").textContent =
        challenge.verification === "review"
          ? "A proof photo or short video is required. The entry remains pending until an administrator approves it."
          : "Proof is optional. This challenge awards points immediately after submission.";
      if (proofInput) proofInput.required = challenge.verification === "review";
      if (proofLabel) {
        proofLabel.textContent =
          challenge.verification === "review"
            ? "Proof photo or short video (required)"
            : "Proof photo or short video (optional)";
      }
      form.elements.challenge_id.value = challenge.id;
      form.elements.value.value = challenge.target_value;
      form.elements.value.min = challenge.target_value;
      form.elements.notes.value = "";
      form.elements.proof.value = "";
      this.setChallengeStatus();
      modal.hidden = false;
    }

    closeChallenge() {
      const modal = this.root.querySelector("[data-challenge-modal]");
      if (modal) modal.hidden = true;
      this.setChallengeStatus();
      this.activeChallenge = null;
    }

    async submitChallenge(event) {
      event.preventDefault();
      if (!this.requireParticipant() || !this.activeChallenge) return;

      const form = event.currentTarget;
      const button = form.querySelector('button[type="submit"]');
      const originalButtonText = button.textContent;
      const proof = form.elements.proof.files?.[0] || null;
      const submittedValue = Number(form.elements.value.value);
      let proofPath = "";

      this.setChallengeStatus();

      if (!Number.isFinite(submittedValue) || submittedValue < Number(this.activeChallenge.target_value)) {
        this.setChallengeStatus(
          `Enter at least ${formatNumber(this.activeChallenge.target_value, 1)} ${this.activeChallenge.unit}.`,
          "error"
        );
        return;
      }

      if (this.activeChallenge.verification === "review" && !proof) {
        this.setChallengeStatus("Add a proof photo or short video before submitting this challenge.", "error");
        return;
      }

      button.disabled = true;

      try {
        if (proof) {
          if (proof.size > 25 * 1024 * 1024) {
            throw new Error("Proof files must be 25 MB or smaller.");
          }
          button.textContent = "Uploading proof…";
          this.setChallengeStatus("Uploading your proof file…");
          const safeName = proof.name.replace(/[^a-z0-9._-]/gi, "-").slice(-80);
          proofPath = `${this.session.user.id}/${crypto.randomUUID()}-${safeName}`;
          const upload = await this.client.storage
            .from("challenge-proofs")
            .upload(proofPath, proof, {
              upsert: false,
              contentType: proofMimeType(proof) || undefined
            });
          if (upload.error) throw upload.error;
        }

        button.textContent = "Saving submission…";
        this.setChallengeStatus("Saving your challenge submission…");
        const result = await this.api("challenge-submit", {
          method: "POST",
          body: {
            challenge_id: this.activeChallenge.id,
            value: submittedValue,
            notes: form.elements.notes.value,
            proof_path: proofPath,
            client_nonce: crypto.randomUUID()
          }
        });

        this.setChallengeStatus(
          result.status === "approved"
            ? `Approved. +${formatNumber(result.points_awarded)} points.`
            : "Submitted successfully and waiting for administrator review.",
          "success"
        );
        await this.loadMe();
        await this.refreshLeaderboards(false);
        window.setTimeout(() => this.closeChallenge(), 1000);
      } catch (error) {
        if (proofPath) {
          await this.client.storage.from("challenge-proofs").remove([proofPath]).catch(() => {});
        }
        this.setChallengeStatus(error.message || "The challenge could not be submitted.", "error");
      } finally {
        button.disabled = false;
        button.textContent = originalButtonText;
      }
    }

    renderGames() {
      const container = this.root.querySelector("[data-games-list]");
      const games = this.bootstrapData?.games || [];
      if (!container) return;

      if (!games.length) {
        container.innerHTML = '<div class="tsc__empty">Games are being added now.</div>';
        return;
      }

      container.innerHTML = games
        .map((game) => {
          const builtIn = GAME_COPY[game.slug] || null;
          const icon = game.icon || builtIn?.icon || "🎮";
          const title = game.name || builtIn?.title || game.slug;
          const description = game.description || builtIn?.description || "Play for your fanbase.";
          const launchUrl = safeGameUrl(game.launch_url);
          let action = '<button type="button" class="tsc__button" disabled>Game setup needed</button>';

          if (launchUrl) {
            action = `<a class="tsc__button" href="${escapeHtml(launchUrl)}">Play now</a>`;
          } else if (builtIn) {
            action = `<button type="button" class="tsc__button" data-game-slug="${escapeHtml(game.slug)}">Play now</button>`;
          }

          return `
            <article class="tsc__card tsc__game-card">
              <span class="tsc__card-icon">${escapeHtml(icon)}</span>
              <div>
                <p class="tsc__kicker">Fan Arcade</p>
                <h2>${escapeHtml(title)}</h2>
                <p>${escapeHtml(description)}</p>
              </div>
              <div class="tsc__meta-row">
                <span class="tsc__meta-chip">${formatNumber(game.daily_attempt_limit)} scored plays per day</span>
                <span class="tsc__meta-chip">Mobile friendly</span>
              </div>
              ${action}
            </article>
          `;
        })
        .join("");
    }

    openGame(slug) {
      if (!this.requireParticipant()) return;
      const game = this.bootstrapData?.games?.find((item) => item.slug === slug);
      const copy = GAME_COPY[slug];
      if (!game || !copy) return;

      this.currentGame = { ...game, ...copy };
      this.gameSessionId = null;
      const modal = this.root.querySelector("[data-game-modal]");
      modal.querySelector("[data-game-title]").textContent = copy.title;
      modal.querySelector("[data-game-instructions]").textContent = copy.instructions;
      modal.querySelector("[data-game-score]").textContent = "0";
      modal.querySelector("[data-game-secondary]").textContent = "Ready";
      modal.querySelector("[data-game-overlay-title]").textContent = "Ready?";
      modal.querySelector("[data-game-overlay-text]").textContent = copy.instructions;
      modal.querySelector('[data-action="start-game"]').textContent = "Start game";
      modal.querySelector("[data-game-overlay]").hidden = false;
      modal.hidden = false;
      this.drawGameBackdrop();
    }

    closeGame() {
      this.gameRunning = false;
      cancelAnimationFrame(this.gameRaf);
      const modal = this.root.querySelector("[data-game-modal]");
      if (modal) modal.hidden = true;
      this.currentGame = null;
      this.gameState = null;
      this.gameSessionId = null;
    }

    async startCurrentGame() {
      if (!this.currentGame || !this.requireParticipant()) return;
      const overlay = this.root.querySelector("[data-game-overlay]");
      const startButton = overlay.querySelector('[data-action="start-game"]');
      startButton.disabled = true;
      overlay.querySelector("[data-game-overlay-title]").textContent = "Starting…";

      try {
        const session = await this.api("game-start", {
          method: "POST",
          body: { game_slug: this.currentGame.slug }
        });
        this.gameSessionId = session.session_id;
      } catch (error) {
        this.showStatus(error.message, "error");
        overlay.querySelector("[data-game-overlay-title]").textContent = "Could not start";
        startButton.disabled = false;
        return;
      }

      startButton.disabled = false;
      overlay.hidden = true;
      this.gameRunning = true;
      this.lastFrame = performance.now();

      if (this.currentGame.slug === "catch-the-punt") this.initCatchGame();
      if (this.currentGame.slug === "running-back") this.initRunningGame();
      if (this.currentGame.slug === "field-goal") this.initFieldGoalGame();
      if (this.currentGame.slug === "tackle-game") this.initTackleGame();

      this.gameRaf = requestAnimationFrame((time) => this.gameLoop(time));
    }

    gameLoop(time) {
      if (!this.gameRunning || !this.currentGame) return;
      const dt = Math.min(0.05, (time - this.lastFrame) / 1000);
      this.lastFrame = time;

      if (this.currentGame.slug === "catch-the-punt") this.updateCatch(dt);
      if (this.currentGame.slug === "running-back") this.updateRunning(dt);
      if (this.currentGame.slug === "field-goal") this.updateFieldGoal(dt);
      if (this.currentGame.slug === "tackle-game") this.updateTackle(dt);

      if (this.gameRunning) {
        this.gameRaf = requestAnimationFrame((nextTime) => this.gameLoop(nextTime));
      }
    }

    canvasPoint(event) {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * this.canvas.width,
        y: ((event.clientY - rect.top) / rect.height) * this.canvas.height
      };
    }

    onGamePointerDown(event) {
      if (!this.gameRunning || !this.gameState) return;
      const point = this.canvasPoint(event);
      this.canvas.setPointerCapture?.(event.pointerId);

      if (this.currentGame.slug === "field-goal" && !this.gameState.kickInFlight) {
        this.gameState.dragStart = point;
        this.gameState.dragCurrent = point;
      }
      if (this.currentGame.slug === "tackle-game" && !this.gameState.cooldown) {
        this.attemptTackle();
      }
      if (["catch-the-punt", "running-back"].includes(this.currentGame.slug)) {
        this.gameState.playerX = point.x;
      }
    }

    onGamePointerMove(event) {
      if (!this.gameRunning || !this.gameState) return;
      const point = this.canvasPoint(event);
      if (["catch-the-punt", "running-back"].includes(this.currentGame.slug)) {
        this.gameState.playerX = Math.max(55, Math.min(this.canvas.width - 55, point.x));
      }
      if (this.currentGame.slug === "field-goal" && this.gameState.dragStart) {
        this.gameState.dragCurrent = point;
      }
    }

    onGamePointerUp(event) {
      if (!this.gameRunning || !this.gameState) return;
      if (this.currentGame.slug === "field-goal" && this.gameState.dragStart) {
        const end = this.canvasPoint(event);
        this.takeKick(this.gameState.dragStart, end);
        this.gameState.dragStart = null;
        this.gameState.dragCurrent = null;
      }
    }

    onGameKey(event) {
      if (!this.gameRunning || !this.gameState) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.gameState.playerX = Math.max(55, this.gameState.playerX - 55);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.gameState.playerX = Math.min(this.canvas.width - 55, this.gameState.playerX + 55);
      }
      if ((event.key === " " || event.key === "Enter") && this.currentGame.slug === "tackle-game") {
        event.preventDefault();
        if (!this.gameState.cooldown) this.attemptTackle();
      }
    }

    setGameHud(score, secondary) {
      this.root.querySelector("[data-game-score]").textContent = formatNumber(score);
      this.root.querySelector("[data-game-secondary]").textContent = secondary;
    }

    drawField() {
      const { ctx, canvas } = this;
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#236447");
      gradient.addColorStop(1, "#0f3d2d");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = "rgba(255,255,255,0.25)";
      ctx.lineWidth = 4;
      for (let y = 80; y < canvas.height; y += 110) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.font = "bold 28px sans-serif";
      ctx.textAlign = "center";
      for (let y = 105, number = 10; y < canvas.height - 60; y += 220, number += 10) {
        ctx.fillText(String(number), 45, y);
        ctx.fillText(String(number), canvas.width - 45, y);
      }
    }

    drawGameBackdrop() {
      if (!this.ctx) return;
      this.drawField();
      this.ctx.fillStyle = "rgba(255,255,255,0.9)";
      this.ctx.font = "bold 54px sans-serif";
      this.ctx.textAlign = "center";
      this.ctx.fillText("TREASURE STATE", this.canvas.width / 2, this.canvas.height / 2);
    }

    drawPlayer(x, y, color = "#f2a900") {
      const ctx = this.ctx;
      ctx.save();
      ctx.translate(x, y);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(0, -32, 18, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(-27, -12, 54, 62);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 25px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("1", 0, 27);
      ctx.fillStyle = "#17232b";
      ctx.fillRect(-34, 48, 24, 15);
      ctx.fillRect(10, 48, 24, 15);
      ctx.restore();
    }

    drawFootball(x, y, rotation = 0) {
      const ctx = this.ctx;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.fillStyle = "#8a4b24";
      ctx.beginPath();
      ctx.ellipse(0, 0, 19, 34, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(-8, -3);
      ctx.lineTo(8, -3);
      ctx.stroke();
      for (let xLine = -6; xLine <= 6; xLine += 4) {
        ctx.beginPath();
        ctx.moveTo(xLine, -8);
        ctx.lineTo(xLine, 2);
        ctx.stroke();
      }
      ctx.restore();
    }

    initCatchGame() {
      this.gameState = {
        remaining: 30,
        playerX: this.canvas.width / 2,
        balls: [],
        spawnTimer: 0.2,
        score: 0,
        catches: 0,
        misses: 0
      };
    }

    updateCatch(dt) {
      const state = this.gameState;
      state.remaining -= dt;
      state.spawnTimer -= dt;

      if (state.spawnTimer <= 0) {
        state.balls.push({
          x: 50 + Math.random() * (this.canvas.width - 100),
          y: -50,
          speed: 300 + Math.random() * 170,
          rotation: Math.random() * Math.PI
        });
        state.spawnTimer = Math.max(0.5, 0.92 - (30 - state.remaining) * 0.008);
      }

      state.balls.forEach((ball) => {
        ball.y += ball.speed * dt;
        ball.rotation += dt * 3;
      });

      state.balls = state.balls.filter((ball) => {
        const caught =
          Math.abs(ball.x - state.playerX) < 58 &&
          ball.y > 735 &&
          ball.y < 850;
        if (caught) {
          state.catches += 1;
          state.score = Math.min(this.currentGame.max_score, state.score + 50);
          return false;
        }
        if (ball.y > this.canvas.height + 50) {
          state.misses += 1;
          return false;
        }
        return true;
      });

      this.drawField();
      state.balls.forEach((ball) => this.drawFootball(ball.x, ball.y, ball.rotation));
      this.drawPlayer(state.playerX, 800);
      this.setGameHud(state.score, `${Math.ceil(state.remaining)} sec · ${state.catches} catches`);

      if (state.remaining <= 0) {
        this.finishGame(state.score, { catches: state.catches, misses: state.misses });
      }
    }

    initRunningGame() {
      this.gameState = {
        remaining: 30,
        playerX: this.canvas.width / 2,
        defenders: [],
        spawnTimer: 0.35,
        score: 0,
        yards: 0,
        hits: 0
      };
    }

    updateRunning(dt) {
      const state = this.gameState;
      state.remaining -= dt;
      state.spawnTimer -= dt;
      state.yards += dt * 4.5;
      state.score = Math.min(
        this.currentGame.max_score,
        Math.floor(state.yards * 4) + Math.max(0, state.defenders.length * 2)
      );

      if (state.spawnTimer <= 0) {
        state.defenders.push({
          x: 60 + Math.random() * (this.canvas.width - 120),
          y: -80,
          speed: 330 + Math.random() * 180,
          passed: false
        });
        state.spawnTimer = Math.max(0.38, 0.78 - (30 - state.remaining) * 0.01);
      }

      state.defenders.forEach((defender) => {
        defender.y += defender.speed * dt;
      });

      state.defenders = state.defenders.filter((defender) => {
        const collision =
          Math.abs(defender.x - state.playerX) < 54 &&
          Math.abs(defender.y - 790) < 62;
        if (collision) {
          state.hits += 1;
          state.yards = Math.max(0, state.yards - 8);
          return false;
        }
        if (defender.y > this.canvas.height + 60) {
          state.yards += 5;
          return false;
        }
        return true;
      });

      this.drawField();
      state.defenders.forEach((defender) => this.drawPlayer(defender.x, defender.y, "#7a0019"));
      this.drawPlayer(state.playerX, 800, "#f2a900");
      this.setGameHud(state.score, `${Math.ceil(state.remaining)} sec · ${Math.floor(state.yards)} yards`);

      if (state.remaining <= 0) {
        const finalScore = Math.min(
          this.currentGame.max_score,
          Math.max(0, Math.floor(state.yards * 8) - state.hits * 25)
        );
        this.finishGame(finalScore, { yards: Math.floor(state.yards), hits: state.hits });
      }
    }

    initFieldGoalGame() {
      this.gameState = {
        attempts: 0,
        made: 0,
        score: 0,
        wind: Math.round((Math.random() * 16 - 8) * 10) / 10,
        dragStart: null,
        dragCurrent: null,
        kickInFlight: false,
        message: "Swipe upward from the ball"
      };
    }

    updateFieldGoal() {
      const state = this.gameState;
      this.drawField();
      const ctx = this.ctx;
      const center = this.canvas.width / 2;

      ctx.strokeStyle = "#ffd43b";
      ctx.lineWidth = 15;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(center, 360);
      ctx.lineTo(center, 150);
      ctx.moveTo(center - 150, 150);
      ctx.lineTo(center - 150, 40);
      ctx.moveTo(center + 150, 150);
      ctx.lineTo(center + 150, 40);
      ctx.moveTo(center - 150, 150);
      ctx.lineTo(center + 150, 150);
      ctx.stroke();

      this.drawFootball(center, 760, Math.PI / 2);

      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.font = "bold 28px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`Wind ${state.wind > 0 ? "→" : "←"} ${Math.abs(state.wind)} mph`, center, 430);
      ctx.font = "24px sans-serif";
      ctx.fillText(state.message, center, 490);

      if (state.dragStart && state.dragCurrent) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 8;
        ctx.beginPath();
        ctx.moveTo(state.dragStart.x, state.dragStart.y);
        ctx.lineTo(state.dragCurrent.x, state.dragCurrent.y);
        ctx.stroke();
      }

      this.setGameHud(state.score, `${state.attempts}/5 kicks · ${state.made} made`);
    }

    takeKick(start, end) {
      const state = this.gameState;
      if (state.kickInFlight || state.attempts >= 5) return;

      const dx = end.x - start.x;
      const dy = start.y - end.y;
      if (dy < 70) {
        state.message = "Swipe farther upward";
        return;
      }

      state.kickInFlight = true;
      state.attempts += 1;

      const windPush = state.wind * 5.5;
      const targetX = this.canvas.width / 2 + dx * 0.75 + windPush;
      const power = Math.min(1, dy / 430);
      const inside = targetX > this.canvas.width / 2 - 142 && targetX < this.canvas.width / 2 + 142;
      const enoughPower = power > 0.42;
      const made = inside && enoughPower;

      if (made) {
        state.made += 1;
        const accuracy = Math.max(0, 1 - Math.abs(targetX - this.canvas.width / 2) / 142);
        const kickPoints = Math.round(120 + accuracy * 80);
        state.score = Math.min(1000, state.score + kickPoints);
        state.message = `GOOD! +${kickPoints}`;
      } else {
        state.message = enoughPower ? "Wide!" : "Short!";
      }

      window.setTimeout(() => {
        state.kickInFlight = false;
        if (state.attempts >= 5) {
          this.finishGame(state.score, { made: state.made, attempts: 5 });
        } else {
          state.wind = Math.round((Math.random() * 16 - 8) * 10) / 10;
          state.message = "Swipe again";
        }
      }, 1100);
    }

    initTackleGame() {
      this.gameState = {
        attempts: 0,
        score: 0,
        carrierX: 70,
        direction: 1,
        speed: 255,
        cooldown: false,
        message: "Tap inside the center zone"
      };
    }

    updateTackle(dt) {
      const state = this.gameState;
      if (!state.cooldown) {
        state.carrierX += state.speed * state.direction * dt;
        if (state.carrierX > this.canvas.width - 70) {
          state.carrierX = this.canvas.width - 70;
          state.direction = -1;
        }
        if (state.carrierX < 70) {
          state.carrierX = 70;
          state.direction = 1;
        }
      }

      this.drawField();
      const ctx = this.ctx;
      const center = this.canvas.width / 2;
      ctx.fillStyle = "rgba(255,215,0,0.22)";
      ctx.fillRect(center - 85, 0, 170, this.canvas.height);
      ctx.strokeStyle = "#ffd43b";
      ctx.lineWidth = 6;
      ctx.strokeRect(center - 85, 0, 170, this.canvas.height);
      this.drawPlayer(state.carrierX, 520, "#7a0019");

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 27px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(state.message, center, 690);
      this.setGameHud(state.score, `${state.attempts}/10 attempts`);
    }

    attemptTackle() {
      const state = this.gameState;
      if (state.cooldown || state.attempts >= 10) return;

      state.cooldown = true;
      state.attempts += 1;
      const distance = Math.abs(state.carrierX - this.canvas.width / 2);
      const points = Math.max(0, Math.round(100 - distance * 0.55));
      state.score = Math.min(1000, state.score + points);
      state.message = points >= 80 ? `Perfect stop! +${points}` : points >= 40 ? `Contact! +${points}` : `Missed angle +${points}`;

      window.setTimeout(() => {
        if (state.attempts >= 10) {
          this.finishGame(state.score, { attempts: 10 });
          return;
        }
        state.carrierX = Math.random() > 0.5 ? 70 : this.canvas.width - 70;
        state.direction = state.carrierX === 70 ? 1 : -1;
        state.speed = 235 + Math.random() * 180;
        state.message = "Tap inside the center zone";
        state.cooldown = false;
      }, 800);
    }

    async finishGame(score, metadata) {
      if (!this.gameRunning) return;
      this.gameRunning = false;
      cancelAnimationFrame(this.gameRaf);

      const finalScore = Math.max(0, Math.min(this.currentGame.max_score, Math.round(score)));
      this.setGameHud(finalScore, "Submitting score…");
      const overlay = this.root.querySelector("[data-game-overlay]");
      const title = overlay.querySelector("[data-game-overlay-title]");
      const text = overlay.querySelector("[data-game-overlay-text]");
      const button = overlay.querySelector('[data-action="start-game"]');
      overlay.hidden = false;
      title.textContent = `${formatNumber(finalScore)} points`;
      text.textContent = "Saving your scored attempt…";
      button.hidden = true;

      try {
        const result = await this.api("game-submit", {
          method: "POST",
          body: {
            game_slug: this.currentGame.slug,
            score: finalScore,
            session_id: this.gameSessionId,
            metadata,
            client_nonce: crypto.randomUUID()
          }
        });

        text.textContent = `Score accepted. +${formatNumber(result.points_awarded)} rivalry points. ${formatNumber(result.attempts_remaining)} scored attempts remain today.`;
        this.setGameHud(finalScore, `+${formatNumber(result.points_awarded)} rivalry points`);
        await this.loadMe();
        await this.refreshLeaderboards(false);
      } catch (error) {
        text.textContent = error.message;
        this.setGameHud(finalScore, "Score not accepted");
      } finally {
        button.hidden = false;
        button.disabled = false;
        button.textContent = "Play again";
      }
    }

    async refreshLeaderboards(showMessage = true) {
      try {
        const payload = await this.api("leaderboards");
        if (payload?.leaderboards) {
          this.bootstrapData.leaderboards = payload.leaderboards;
          this.renderScoreboard();
          this.renderLeaderboard();
          this.renderImpactSummary();
        }
        if (showMessage) this.showStatus("Leaderboards refreshed.", "success");
      } catch (error) {
        this.showStatus(error.message, "error");
      }
    }

    renderLeaderboard() {
      const container = this.root.querySelector("[data-leaderboard-container]");
      const data = this.bootstrapData?.leaderboards;
      if (!container) return;

      let rows = [];
      let headers = [];
      let rowTemplate = null;

      if (this.activeLeaderTab === "individual") {
        rows = data?.individuals || [];
        headers = ["Rank", "Fan", "Team", "Points"];
        rowTemplate = (row) => `
          <tr>
            <td class="tsc__rank">#${formatNumber(row.rank)}</td>
            <td>${escapeHtml(row.display_name)}</td>
            <td>${escapeHtml(row.school_code)}</td>
            <td><strong>${formatNumber(row.total_points)}</strong></td>
          </tr>
        `;
      }

      if (this.activeLeaderTab === "trivia") {
        rows = data?.trivia || [];
        headers = ["Rank", "Fan", "Team", "Correct", "Points"];
        rowTemplate = (row) => `
          <tr>
            <td class="tsc__rank">#${formatNumber(row.rank)}</td>
            <td>${escapeHtml(row.display_name)}</td>
            <td>${escapeHtml(row.school_code)}</td>
            <td>${formatNumber(row.correct_answers)}/${formatNumber(row.questions_answered)}</td>
            <td><strong>${formatNumber(row.trivia_points)}</strong></td>
          </tr>
        `;
      }

      if (this.activeLeaderTab === "games") {
        rows = data?.games || [];
        headers = ["Rank", "Game", "Fan", "Team", "Best"];
        rowTemplate = (row) => `
          <tr>
            <td class="tsc__rank">#${formatNumber(row.rank)}</td>
            <td>${escapeHtml(row.game_name)}</td>
            <td>${escapeHtml(row.display_name)}</td>
            <td>${escapeHtml(row.school_code)}</td>
            <td><strong>${formatNumber(row.best_score)}</strong></td>
          </tr>
        `;
      }

      if (!rows.length) {
        container.innerHTML =
          '<div class="tsc__empty">The first completed activity will start this leaderboard.</div>';
        return;
      }

      container.innerHTML = `
        <table class="tsc__table">
          <thead><tr>${headers.map((header) => `<th>${header}</th>`).join("")}</tr></thead>
          <tbody>${rows.slice(0, 50).map(rowTemplate).join("")}</tbody>
        </table>
      `;
    }

    renderImpactSummary() {
      const container = this.root.querySelector("[data-impact-summary]");
      if (!container) return;

      const totals = this.bootstrapData?.leaderboards?.impact || [];
      if (!totals.length) {
        container.innerHTML = "<p>Approved fitness and community totals will appear here.</p>";
        return;
      }

      container.innerHTML = totals
        .slice(0, 8)
        .map(
          (item) => `
            <div class="tsc__impact-stat">
              <strong>${formatNumber(item.total_value, 1)}</strong>
              <span>${escapeHtml(item.unit)} · ${escapeHtml(item.title)}</span>
            </div>
          `
        )
        .join("");
    }

    showStatus(message, type = "", duration = 4500) {
      const status = this.root.querySelector("[data-status]");
      if (!status) return;
      window.clearTimeout(this.statusTimer);
      status.textContent = message;
      status.classList.toggle("is-error", type === "error");
      status.classList.toggle("is-success", type === "success");
      status.hidden = false;

      if (duration > 0) {
        this.statusTimer = window.setTimeout(() => {
          status.hidden = true;
        }, duration);
      }
    }
  }

  const boot = () => {
    document.querySelectorAll("[data-tsc-root]").forEach((root) => {
      if (root.dataset.tscInitialized === "true") return;
      root.dataset.tscInitialized = "true";
      const app = new TreasureStateChallenge(root);
      app.init().catch((error) => {
        console.error("Treasure State Challenge failed to initialize:", error);
        const status = root.querySelector("[data-status]");
        if (status) {
          status.textContent = error.message;
          status.classList.add("is-error");
          status.hidden = false;
        }
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
})();
